import { checkResponseStatus } from "@api/Utils"
import { EntityListResponseQueueEntryExecutionReportAttachment, EntityListResponseQueueEntryObject, EntitySingleResponseQueueEntryObject, ExportPayloadExporterIdEnum, QueueEntryObject, QueueList } from "cs.node.utils/csapi/provisioning"
import { QueueEntryObjectStatusEnum } from "@utils/QueueUtils"
import axios, { AxiosResponse } from "axios"

type queuesEntriesByFilterPayload = {
    queueId: string,
    externalKey?: string,
    searchKey?: string,
    pageIndex?: number,
    pageSize?: number,
    sort?: string,
    status?: QueueEntryObjectStatusEnum[],
    updateDateFrom?: string,
    updateDateTo?: string
}

const getQueuesEntriesByFilter = async (payload: queuesEntriesByFilterPayload, controller?: AbortController) =>{
    const { queueId, externalKey, searchKey, pageIndex, pageSize, sort, status, updateDateFrom, updateDateTo } = payload
    try {
        const response:AxiosResponse<EntityListResponseQueueEntryObject,any> = await axios.get(`api/queues/${queueId}/listEntriesByFilter`, {
            params: {
                searchKey,
                externalKey,
                pageIndex,
                pageSize,
                sort,
                status,
                updateDateFrom,
                updateDateTo
            },
            signal: controller?.signal
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

const getExportsByFilter = async (payload: Omit<queuesEntriesByFilterPayload, "queueId"> & {exporterId?: ExportPayloadExporterIdEnum, userId?: string }, controller?: AbortController) =>{
    const { exporterId, userId, externalKey, searchKey, pageIndex, pageSize, sort, status, updateDateFrom, updateDateTo } = payload
    try {
        const response:AxiosResponse<EntityListResponseQueueEntryObject,any> = await axios.get(`api/queues/listExportsByFilter`, {
            params: {
                exporterId,
                userId,
                searchKey,
                externalKey,
                pageIndex,
                pageSize,
                sort,
                status,
                updateDateFrom,
                updateDateTo
            },
            signal: controller?.signal
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

const getReportsByFilter = async (payload: Omit<queuesEntriesByFilterPayload, "queueId"> & {reportIdentifier?: string, shopId?: string }, controller?: AbortController) =>{
    const { reportIdentifier, shopId, externalKey, searchKey, pageIndex, pageSize, sort, status, updateDateFrom, updateDateTo } = payload
    try {
        const response:AxiosResponse<EntityListResponseQueueEntryObject,any> = await axios.get(`api/queues/listReportsByFilter`, {
            params: {
                reportIdentifier,
                shopId,
                searchKey,
                externalKey,
                pageIndex,
                pageSize,
                sort,
                status,
                updateDateFrom,
                updateDateTo
            },
            signal: controller?.signal
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

type updateQueueEntryStatusPayload = {
    queueId: string,
    entryId: string,
    newStatus: QueueEntryObjectStatusEnum
}

const updateQueueEntryStatus = async(payload: updateQueueEntryStatusPayload)=> {
    const { queueId, entryId, newStatus } = payload
    try {
        const response:AxiosResponse<EntityListResponseQueueEntryObject,any> = await axios.put(`api/queues/${queueId}/${entryId}/status`, {newStatus})
        console.log(response)
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

const updateQueueBulkEntriesStatus = async(queueId: string, entryIds: string[], newStatus: QueueEntryObjectStatusEnum) => {
    try {
        const response:AxiosResponse<PromiseSettledResult<AxiosResponse<EntitySingleResponseQueueEntryObject, any>>[],any> = await axios.put(`api/queues/${queueId}/bulk/status`, {entryIds, newStatus})
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

const getQueues = async (): Promise<QueueList> =>{
    try {
        const response:AxiosResponse<QueueList,any> = await axios.get('api/queues')
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

type getQueueEntryPayload = {
    queueId: string,
    entryId: string
}

const getQueueEntry = async(payload: getQueueEntryPayload)=> {
    const { queueId, entryId } = payload
    try {
        const response:AxiosResponse<EntitySingleResponseQueueEntryObject,any> = await axios.get(`api/queues/${queueId}/${entryId}`)
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

const getQueueEntryAttachments = async(payload: getQueueEntryPayload)=> {
    const { queueId, entryId } = payload
    try {
        const response:AxiosResponse<EntityListResponseQueueEntryExecutionReportAttachment,any> = await axios.get(`api/queues/${queueId}/${entryId}/attachments`)
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

const getQueueEntryAttachment = async(queueId: string, entryId: string, filename:string)=>{
    try{
        const response:AxiosResponse<any,any> = await axios.get(`api/queues/${queueId}/${entryId}/attachments/${encodeURIComponent(filename)}`, {
            responseType: 'blob'
        })
        return response.data
    } catch(error:any) {
        checkResponseStatus(error)
        throw error
    }
}

const createQueueEntry = async(queueId: string, entry: QueueEntryObject) =>{
    try {
        const response:AxiosResponse<EntitySingleResponseQueueEntryObject,any> = await axios.post(`api/queues/${queueId}/`, { entry })
        return response.data
    } catch(error: any) {
        checkResponseStatus(error)
        throw error
    }
}

export { 
    getQueuesEntriesByFilter,
    getReportsByFilter,
    getExportsByFilter,
    updateQueueEntryStatus,
    updateQueueBulkEntriesStatus,
    getQueues,
    getQueueEntry,
    getQueueEntryAttachments,
    getQueueEntryAttachment,
    createQueueEntry
}